import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { collection, query, onSnapshot, serverTimestamp, doc } from '../firebase.config';
import { enforcedAddDoc as addDoc, enforcedSetDoc as setDoc, enforcedDeleteDoc as deleteDoc } from '../utils/enforcedTracking';
import { useLanguage } from '../contexts/LanguageContext';
import { useTableSort } from '../utils/tableSort';
import { useTablePagination } from '../utils/tablePagination';
import { roundTo2Decimals } from '../utils/numberFormat';
import { withMPINProtection } from './MPINProtectedRoute';
import { useEnhancedCompany } from '../contexts/EnhancedCompanyContext';
import { getCollectionForCompany, normalizeCompanyDocSnapshot } from '../utils/dataAdapter';
import { useCompanyCurrency } from '../hooks/useCompanyCurrency';
import { getCurrencySymbol } from '../utils/currencyUtils';
import { useNotification } from '../contexts/NotificationContext';
import { secureCacheManager } from '../utils/secureCacheManager';
import LoadingScreen from './LoadingScreen';
import { useComponentLoading } from '../contexts/LoadingContext';
import { parseDefaultServiceTagsInput } from '../utils/serviceTagRegistry';
import ItemsCatalogTab from './Items/ItemsCatalogTab';
import { ITEMS_THEME } from './Items/itemsTheme';
import { Package } from 'lucide-react';

const Items = ({ db, userId, isAuthReady, appId }) => {
    const { currentCompany } = useEnhancedCompany();
    const companyId = currentCompany?.companyId || currentCompany?.id;
    const { currency: companyCurrency } = useCompanyCurrency();
    const { t } = useLanguage();
    const { success, error, warning, info } = useNotification();
    const [itemName, setItemName] = useState('');
    const [quantityMeasurement, setQuantityMeasurement] = useState('');
    const [defaultRate, setDefaultRate] = useState('');
    const location = useLocation();
    const [itemType, setItemType] = useState(() => {
        const p = new URLSearchParams(location.search).get('type');
        return p === 'Goods' ? 'Goods' : 'Services';
    });
    const [hsnCode, setHsnCode] = useState('');
    const [gstPercentage, setGstPercentage] = useState('');
    const [message, setMessage] = useState('');
    const [itemsList, setItemsList] = useState([]);
    const [editingItemId, setEditingItemId] = useState(null);
    const [description, setDescription] = useState('');
    const [defaultServiceTagsInput, setDefaultServiceTagsInput] = useState('');
    const [purchasePrice, setPurchasePrice] = useState('0');
    const [salePrice, setSalePrice] = useState('0');
    const [openingStock, setOpeningStock] = useState('0');
    const [gstSplitRate, setGstSplitRate] = useState('0');
    const [isActive, setIsActive] = useState(true);
    const [purchaseBills, setPurchaseBills] = useState([]);
    const [salesBills, setSalesBills] = useState([]);
    const [stockMap, setStockMap] = useState({});
    // Add new state for composition GST and raw material
    const [compositionGstRate, setCompositionGstRate] = useState('');
    const [isRawMaterial, setIsRawMaterial] = useState(false);
    const [rawMaterialType, setRawMaterialType] = useState('Base Material');
    const [category, setCategory] = useState('');
    const [customCategory, setCustomCategory] = useState('');
    const [barcodeNumber, setBarcodeNumber] = useState('');
    const [companyCategories, setCompanyCategories] = useState([]);
    const [companyDetails, setCompanyDetails] = useState(null);
    
    // Loading state - set to TRUE immediately on mount, before any data operations
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    
    // Track if we've shown loading screen at least once (minimum display time)
    const [loadingStartTime, setLoadingStartTime] = useState(null);
    const MIN_LOADING_TIME = 500; // Minimum 500ms to show loading screen
    
    // Use loading context for better UX - MUST be defined before checkAndStopInitialLoading
    const { startLoading, stopLoading } = useComponentLoading('items', 'Loading items...');
    
    // Track which data sources have loaded
    const dataLoadedRef = useRef({
        items: false
    });
    const formSectionRef = useRef(null);
    
    // Function to check if all required data is loaded and stop initial loading
    const checkAndStopInitialLoading = useCallback(() => {
        const { items } = dataLoadedRef.current;
        if (items) {
            const elapsed = loadingStartTime ? Date.now() - loadingStartTime : 0;
            const remainingTime = Math.max(0, MIN_LOADING_TIME - elapsed);
            
            if (remainingTime > 0) {
                console.log(`ðŸ”„ [LOADING SCREEN] Items: All data loaded, waiting ${remainingTime}ms more for minimum display time`);
                setTimeout(() => {
                    console.log('ðŸ”„ [LOADING SCREEN] Items: Stopping initial loading - all data loaded and minimum time elapsed');
                    setIsInitialLoading(false);
                    stopLoading();
                }, remainingTime);
            } else {
                console.log('ðŸ”„ [LOADING SCREEN] Items: Stopping initial loading - all data loaded');
                setIsInitialLoading(false);
                stopLoading();
            }
        }
    }, [loadingStartTime, stopLoading]);

    // Filter states
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState('All');
    const [filterGstRate, setFilterGstRate] = useState('All');
    const [filterActive, setFilterActive] = useState('All');

    // Handle filter changes from CompactFilterBar
    const handleFilterChange = (filters) => {
        setSearchTerm(filters.search || '');
        setFilterType(filters.type || 'All');
        setFilterGstRate(filters.gstRate || 'All');
        setFilterActive(filters.active || 'All');
    };

    // Clear all filters
    const clearFilters = () => {
        setSearchTerm('');
        setFilterType('All');
        setFilterGstRate('All');
        setFilterActive('All');
    };

    // Sync itemType to URL
    useEffect(() => {
        const params = new URLSearchParams(window.location.hash.split('?')[1] || '');
        if (params.get('type') !== itemType) {
            params.set('type', itemType);
            const base = window.location.hash.split('?')[0] || '#/items';
            const next = `${base}?${params.toString()}`;
            if (window.location.hash !== next) window.location.hash = next;
        }
    }, [itemType]);

    // Filter items based on search term and filters
    const filteredItems = useMemo(() => {
        return itemsList.filter(item => {
            // Search term filter
            const matchesSearch = !searchTerm || 
                item.itemName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.hsnCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.quantityMeasurement?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.barcodeNumber?.toLowerCase().includes(searchTerm.toLowerCase());

            // Type filter
            const matchesType = filterType === 'All' || item.itemType === filterType;

            // GST rate filter
            const matchesGstRate = filterGstRate === 'All' || 
                (filterGstRate === '0' && (item.gstPercentage === 0 || !item.gstPercentage)) ||
                (filterGstRate === '5' && item.gstPercentage === 5) ||
                (filterGstRate === '12' && item.gstPercentage === 12) ||
                (filterGstRate === '18' && item.gstPercentage === 18) ||
                (filterGstRate === '28' && item.gstPercentage === 28);

            // Active status filter
            const matchesActive = filterActive === 'All' || 
                (filterActive === 'Active' && item.isActive !== false) ||
                (filterActive === 'Inactive' && item.isActive === false);

            return matchesSearch && matchesType && matchesGstRate && matchesActive;
        });
    }, [itemsList, searchTerm, filterType, filterGstRate, filterActive]);

    // Table sorting hook with default sort by creation date descending (LIFO)
    const { sortConfig, handleSort, getSortedData } = useTableSort([], { key: 'createdAt', direction: 'desc' });
    
    // Table pagination hook
    const sortedItems = getSortedData(filteredItems);
    const pagination = useTablePagination(sortedItems, 10);

    // 1. Add 'Nos.' to predefinedUnits, alphabetically
    const predefinedUnits = [
        'Bag', 'Barrel', 'Block', 'Board', 'Bottle', 'Box', 'Bundle', 'Can', 'Carton', 'Centimeter', 'Cubic Feet', 'Cubic Meter', 'Cu. Inch', 'Cu. Yard', 'Cylinder', 'Day', 'Drum', 'Foot', 'Gallon', 'Gram', 'Hour', 'Impression', 'Inch', 'Jar', 'Job', 'Kg', 'Litre', 'Lot', 'Meter', 'Millimeter', 'Month', 'Nos.', 'Packet', 'Pair', 'Panel', 'Piece', 'Pieces', 'Plate', 'Quintal', 'Ream', 'Roll', 'Run', 'Set', 'Sheet', 'Slab', 'Sq. Ft.', 'Sq. Inch', 'Sq. Yard', 'Square Meter', 'Strip', 'Ton', 'Tube', 'Year', 'Other'
    ];
    const [customUnit, setCustomUnit] = useState('');

    // 2. Replace <select> with searchable input+dropdown for quantity measurement
    const [unitSearch, setUnitSearch] = useState('');
    const filteredUnits = predefinedUnits.filter(u => u.toLowerCase().includes(unitSearch.toLowerCase())).sort();

    // Start loading IMMEDIATELY on mount and track start time - BEFORE any data operations
    useEffect(() => {
        console.log('ðŸ”„ [LOADING SCREEN] Items: Component mounted - starting loading IMMEDIATELY (before any data operations)');
        const startTime = Date.now();
        setLoadingStartTime(startTime);
        setIsInitialLoading(true);
        startLoading('Loading items...', 'Fetching your product and service catalog');
    }, [startLoading]);

    // Fetch items data from Firestore
    useEffect(() => {
        if (db && userId && isAuthReady && appId && companyId) {
            const itemsCollectionRef = getCollectionForCompany(db, { companyId, appId, userId }, 'items');
            if (!itemsCollectionRef) {
                setIsInitialLoading(false);
                stopLoading();
                return;
            }
            
            const q = query(itemsCollectionRef);

            const unsubscribe = onSnapshot(q, (snapshot) => {
                const items = [];
                const seenIds = new Set();
                
                snapshot.forEach((doc) => {
                    const item = { id: doc.id, ...doc.data() };
                    
                    // Debug: Check for duplicate IDs
                    if (seenIds.has(item.id)) {
                        console.warn('Duplicate item ID found:', item.id, item);
                    } else {
                        seenIds.add(item.id);
                    }
                    
                    // Debug: Check for invalid IDs
                    if (!item.id || typeof item.id !== 'string') {
                        console.warn('Invalid item ID found:', item);
                    }
                    
                    items.push(item);
                });
                
                console.log('Loaded items count:', items.length, 'Unique IDs:', seenIds.size);
                
                // LIFO sorting is now handled by the pagination utility
                setItemsList(items);
                
                // Only stop loading if we actually got data or confirmed there's no data
                if (items.length > 0 || snapshot.empty) {
                    setIsInitialLoading(false);
                    stopLoading();
                }
            }, (error) => {
                console.error("Error fetching items:", error);
                setMessage("Error fetching items. Please try again.");
                setIsInitialLoading(false);
                stopLoading();
            });

            return () => {
                unsubscribe();
            };
        } else {
            setIsInitialLoading(false);
            stopLoading();
        }
    }, [db, userId, isAuthReady, appId, companyId, stopLoading]);

    // Fetch purchase and sales bills for stock calculation
    useEffect(() => {
        if (db && userId && isAuthReady && companyId) {
            const purchasesCollectionRef = getCollectionForCompany(db, { companyId, appId, userId }, 'purchaseBills');
            const salesCollectionRef = getCollectionForCompany(db, { companyId, appId, userId }, 'salesBills');
            
            if (!purchasesCollectionRef || !salesCollectionRef) return;
            
            const unsubscribePurchases = onSnapshot(purchasesCollectionRef, (snapshot) => {
                const bills = [];
                snapshot.forEach((doc) => {
                    bills.push({ id: doc.id, ...doc.data() });
                });
                setPurchaseBills(bills);
            });
            const unsubscribeSales = onSnapshot(salesCollectionRef, (snapshot) => {
                const bills = [];
                snapshot.forEach((doc) => {
                    bills.push({ id: doc.id, ...doc.data() });
                });
                setSalesBills(bills);
            });
            return () => {
                unsubscribePurchases();
                unsubscribeSales();
            };
        }
    }, [db, userId, isAuthReady, appId, companyId]);

    // Fetch live stock from stock collection
    useEffect(() => {
        if (db && userId && isAuthReady && companyId) {
            const stockCollectionRef = getCollectionForCompany(db, { companyId, appId, userId }, 'stock');
            if (!stockCollectionRef) return;
            
            const unsubscribe = onSnapshot(stockCollectionRef, (snapshot) => {
                const stock = {};
                snapshot.forEach((doc) => {
                    stock[doc.id] = doc.data().itemQuantity || 0;
                });
                setStockMap(stock);
            });
            return () => unsubscribe();
        }
    }, [db, userId, isAuthReady, appId, companyId]);

    // Fetch company categories
    useEffect(() => {
        if (db && userId && isAuthReady && companyId) {
            const categoriesRef = getCollectionForCompany(db, { companyId, appId, userId }, 'companyCategories');
            if (!categoriesRef) return;
            
            const unsubscribe = onSnapshot(categoriesRef, (snapshot) => {
                const categories = [];
                snapshot.forEach((doc) => {
                    categories.push(doc.data().categoryName);
                });
                setCompanyCategories(categories);
            });
            return () => unsubscribe();
        }
    }, [db, userId, isAuthReady, appId, companyId]);

    // Fetch company details
    useEffect(() => {
        if (!isAuthReady || !db || db.type === 'mock-firestore' || !companyId) return;

        const companyDocRef = doc(db, 'companies', companyId);
        const unsubscribe = onSnapshot(companyDocRef, (docSnap) => {
            if (docSnap.exists()) {
                setCompanyDetails(normalizeCompanyDocSnapshot(docSnap.data() || {}, companyId));
            }
        });
        return () => unsubscribe();
    }, [db, isAuthReady, companyId]);

    // Function to clear item form fields
    const clearItemForm = () => {
        setItemName('');
        setQuantityMeasurement('');
        setDefaultRate('');
        setItemType('Services');
        setHsnCode('');
        setGstPercentage('');
        setDescription('');
        setDefaultServiceTagsInput('');
        setPurchasePrice('0');
        setSalePrice('0');
        setOpeningStock('0');
        setGstSplitRate('0');
        setIsActive(true);
        setEditingItemId(null);
        setMessage('');
        setCustomUnit('');
        setCompositionGstRate('');
        setIsRawMaterial(false);
        setRawMaterialType('Base Material');
        setCategory('');
        setCustomCategory('');
        setBarcodeNumber('');
    };

    // Function to add custom category to company categories
    const addCustomCategory = async (categoryName) => {
        if (!db || !userId || !companyId) return;
        
        try {
            const categoriesRef = getCollectionForCompany(db, { companyId, appId, userId }, 'companyCategories');
            if (!categoriesRef) return;
            
            // Check if category already exists
            const existingCategories = companyCategories;
            if (existingCategories.includes(categoryName)) {
                return; // Category already exists
            }
            
            // Add new category
            await addDoc(categoriesRef, {
                categoryName: categoryName,
                createdAt: serverTimestamp(),
                createdBy: userId
            });
            
            info(`Category ${categoryName} added successfully!`, { title: 'Category Added', duration: 5000 });
            console.log('Custom category added:', categoryName);
        } catch (error) {
            console.error('Error adding custom category:', error);
        }
    };

    // Handle adding/updating an item
    const handleSaveItem = async () => {
        if (!db || !userId) {
            warning("Firebase not initialized or user not authenticated.", { title: 'Authentication Error' });
            return;
        }
        if (!itemName) {
            warning("Item Name is required.", { title: 'Validation Error' });
            return;
        }
        if (category === 'Other' && !customCategory.trim()) {
            warning("Please enter custom category name.", { title: 'Validation Error' });
            return;
        }
        
        // Add custom category to company categories if it's new
        if (category === 'Other' && customCategory.trim()) {
            await addCustomCategory(customCategory.trim());
        }
        let sgstRate = 0, cgstRate = 0;
        if (parseFloat(gstSplitRate) > 0) {
                    sgstRate = roundTo2Decimals(parseFloat(gstSplitRate) / 2).toFixed(2);
        cgstRate = roundTo2Decimals(parseFloat(gstSplitRate) / 2).toFixed(2);
        }
        try {
            const itemsCollectionRef = getCollectionForCompany(db, { companyId, appId, userId }, 'items');
            if (!itemsCollectionRef) {
                warning("No company selected for saving items.", { title: 'Validation Error' });
                return;
            }
            const itemData = {
                itemName,
                quantityMeasurement,
                defaultRate: parseFloat(defaultRate) || 0,
                itemType: itemType || 'Services',
                hsnCode: hsnCode,
                gstPercentage: parseFloat(gstPercentage) || 0,
                compositionGstRate: parseFloat(compositionGstRate) || 0,
                isRawMaterial: itemType === 'Goods' ? isRawMaterial : false,
                rawMaterialType: itemType === 'Goods' && isRawMaterial ? rawMaterialType : '',
                description,
                purchasePrice: parseFloat(purchasePrice) || 0,
                salePrice: parseFloat(salePrice) || 0,
                openingStock: parseFloat(openingStock) || 0,
                sgstRate: parseFloat(sgstRate) || 0,
                cgstRate: parseFloat(cgstRate) || 0,
                isActive,
                category: category === 'Other' ? customCategory : (category || ''),
                barcodeNumber: barcodeNumber || '',
                defaultServiceTags: parseDefaultServiceTagsInput(defaultServiceTagsInput),
                timestamp: serverTimestamp()
            };

            if (editingItemId) {
                const itemRef = doc(db, 'companies', companyId, 'items', editingItemId);
                await setDoc(itemRef, itemData);
                
                // ðŸ”’ PHASE 3: Invalidate caches after update
                console.log('ðŸ§¹ PHASE 3: Invalidating caches after item update');
                secureCacheManager.invalidate(`items_list_${companyId}`);
                secureCacheManager.invalidate(`dashboard_${companyId}`);
                
                success(
                    `Item ${itemName} updated successfully!`,
                    {
                        title: 'Item Updated',
                        details: `${category === 'Other' ? customCategory : category} â€¢ GST: ${gstPercentage}% â€¢ Type: ${itemType}`,
                        duration: 6000
                    }
                );
            } else {
                await addDoc(itemsCollectionRef, itemData);
                
                // ðŸ”’ PHASE 3: Invalidate caches after add
                console.log('ðŸ§¹ PHASE 3: Invalidating caches after item add');
                secureCacheManager.invalidate(`items_list_${companyId}`);
                secureCacheManager.invalidate(`dashboard_${companyId}`);
                
                success(
                    `Item ${itemName} added successfully!`,
                    {
                        title: 'Item Added',
                        details: `${category === 'Other' ? customCategory : category} â€¢ GST: ${gstPercentage}% â€¢ Type: ${itemType}`,
                        duration: 6000
                    }
                );
            }
            clearItemForm();
        } catch (err) {
            console.error("Error saving item:", err);
            
            // ðŸš« Handle subscription/limit errors
            if (err.code === 'SUBSCRIPTION_EXPIRED' || err.code === 'FEATURE_LIMIT_EXCEEDED') {
                console.log('ðŸš« Subscription expired or limit exceeded:', err);
                // Error notification is already shown by ErrorNotificationService
                return;
            }
            
            error("Error saving item. Please try again.", { title: 'Save Failed', duration: 7000 });
        }
    };

    // Handle editing an item
    const handleEditItem = (item) => {
        setEditingItemId(item.id);
        setItemName(item.itemName);
        setQuantityMeasurement(item.quantityMeasurement);
        setDefaultRate(item.defaultRate);
        setItemType(item.itemType || 'Services');
        setHsnCode(item.hsnCode || '');
        setGstPercentage(item.gstPercentage || '');
        setCompositionGstRate(item.compositionGstRate || '');
        setIsRawMaterial(item.isRawMaterial || false);
        setRawMaterialType(item.rawMaterialType || 'Base Material');
        setDescription(item.description || '');
        setDefaultServiceTagsInput(
            Array.isArray(item.defaultServiceTags) ? item.defaultServiceTags.join(', ') : ''
        );
        setPurchasePrice(item.purchasePrice?.toString() || '0');
        setSalePrice(item.salePrice?.toString() || '0');
        setOpeningStock(item.openingStock?.toString() || '0');
        setGstSplitRate(item.sgstRate && item.cgstRate ? (parseFloat(item.sgstRate) + parseFloat(item.cgstRate)).toString() : '0');
        setIsActive(item.isActive !== false);
        setMessage('Editing existing item.');
        setCustomUnit(predefinedUnits.includes(item.quantityMeasurement) ? '' : (item.quantityMeasurement || ''));
        if (item.category && predefinedCategories.includes(item.category)) {
            setCategory(item.category);
            setCustomCategory('');
        } else if (item.category && companyCategories.includes(item.category)) {
            setCategory(item.category);
            setCustomCategory('');
        } else if (item.category) {
            setCategory('Other');
            setCustomCategory(item.category);
        } else {
            setCategory('');
            setCustomCategory('');
        }
        setBarcodeNumber(item.barcodeNumber || '');
        requestAnimationFrame(() => {
            formSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
    };

    // Handle deleting an item
    const handleDeleteItem = async (itemId) => {
        if (!db || !userId) {
            warning("Firebase not initialized or user not authenticated.", { title: 'Authentication Error' });
            return;
        }
        try {
            // Get item details before deleting
            const itemToDelete = itemsList.find(i => i.id === itemId);
            const itemNameToDelete = itemToDelete?.itemName || 'Unknown Item';
            
            const itemRef = doc(db, 'companies', companyId, 'items', itemId);
            await deleteDoc(itemRef);
            
            // ðŸ”’ PHASE 3: Invalidate caches after delete
            console.log('ðŸ§¹ PHASE 3: Invalidating caches after item delete');
            secureCacheManager.invalidate(`items_list_${companyId}`);
            secureCacheManager.invalidate(`dashboard_${companyId}`);
            
            success(
                `Item ${itemNameToDelete} deleted successfully!`,
                {
                    title: 'Item Deleted',
                    duration: 6000
                }
            );
        } catch (err) {
            console.error("Error deleting item:", err);
            
            // ðŸš« Handle subscription/limit errors
            if (err.code === 'SUBSCRIPTION_EXPIRED' || err.code === 'FEATURE_LIMIT_EXCEEDED') {
                console.log('ðŸš« Subscription expired or limit exceeded:', err);
                // Error notification is already shown by ErrorNotificationService
                return;
            }
            
            error("Error deleting item. Please try again.", { title: 'Delete Failed', duration: 7000 });
        }
    };

    // Calculate stock for each item
    const getStockForItem = (itemId) => {
        let purchased = 0;
        let sold = 0;
        purchaseBills.forEach(bill => {
            if (bill.items && Array.isArray(bill.items)) {
                bill.items.forEach(item => {
                    if (item.itemId === itemId) {
                        purchased += parseFloat(item.quantity) || 0;
                    }
                });
            }
        });
        salesBills.forEach(bill => {
            if (bill.items && Array.isArray(bill.items)) {
                bill.items.forEach(item => {
                    if (item.itemId === itemId) {
                        sold += parseFloat(item.nos) || 0;
                    }
                });
            }
        });
        return purchased - sold;
    };

    // SGST/CGST vs IGST logic
    const sgstCgstUsed = parseFloat(gstSplitRate) > 0;

    const gstRateOptions = ['0', '0.25', '3', '5', '12', '18', '28'];
    
    // Predefined categories
    const predefinedCategories = [
        'Electronics', 'Clothing & Textiles', 'Food & Beverages', 'Home & Garden', 
        'Health & Beauty', 'Sports & Fitness', 'Books & Media', 'Automotive', 
        'Office Supplies', 'Tools & Hardware', 'Toys & Games', 'Jewelry & Accessories', 
        'Pet Supplies'
    ];

    const [showMobileFilters, setShowMobileFilters] = useState(false);
    const [expandedCards, setExpandedCards] = useState({});
    
    // Bulk delete states
    const [selectedForDeletion, setSelectedForDeletion] = useState(new Set());
    const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
    const [isBulkDeleting, setIsBulkDeleting] = useState(false);

    // Bulk delete functions
    const handleDeleteSelection = (itemId) => {
        const newSelection = new Set(selectedForDeletion);
        if (newSelection.has(itemId)) {
            newSelection.delete(itemId);
        } else {
            newSelection.add(itemId);
        }
        setSelectedForDeletion(newSelection);
    };

    const handleSelectAllForDeletion = () => {
        if (selectedForDeletion.size === filteredItems.length && filteredItems.length > 0) {
            setSelectedForDeletion(new Set());
        } else {
            setSelectedForDeletion(new Set(filteredItems.map(item => item.id)));
        }
    };

    const handleBulkDelete = async () => {
        if (selectedForDeletion.size === 0) {
            warning('Please select items to delete', { title: 'No Selection' });
            return;
        }

        setIsBulkDeleting(true);
        try {
            const itemsRef = getCollectionForCompany(db, { companyId, appId, userId }, 'items');
            
            const deletePromises = Array.from(selectedForDeletion).map(itemId => {
                const itemRef = doc(itemsRef, itemId);
                return deleteDoc(itemRef);
            });

            await Promise.all(deletePromises);
            
            setSelectedForDeletion(new Set());
            setShowBulkDeleteConfirm(false);
            
            success(`Successfully deleted ${selectedForDeletion.size} item(s)`, { title: 'Items Deleted', duration: 6000 });
        } catch (error) {
            console.error('Bulk delete error:', error);
            error('Failed to delete items: ' + error.message, { title: 'Delete Failed', duration: 7000 });
        } finally {
            setIsBulkDeleting(false);
        }
    };

    // Export helpers
    const getExportData = useMemo(() => {
        return sortedItems.map((item) => {
            const opening = parseFloat(item.openingStock) || 0;
            const allPurchases = purchaseBills
                .flatMap(bill => (bill.rows || []).filter(row => row.item === item.id))
                .reduce((sum, row) => sum + (parseFloat(row.qty) || 0), 0);
            const allSales = salesBills
                .flatMap(bill => (bill.rows || []).filter(row => row.item === item.id))
                .reduce((sum, row) => sum + (parseFloat(row.qty) || 0), 0);
            const liveStock = opening + allPurchases - allSales;

            return {
                itemName: item.itemName,
                itemType: item.itemType,
                unit: item.quantityMeasurement,
                rate: `${getCurrencySymbol(companyCurrency)}${item.defaultRate}`,
                hsnCode: item.hsnCode || 'N/A',
                gstPercentage: `${item.gstPercentage || 0}%`,
                stock: liveStock,
                category: item.category || 'N/A',
                barcode: item.barcodeNumber || 'N/A',
                active: item.isActive !== false ? 'Active' : 'Inactive'
            };
        });
    }, [sortedItems, purchaseBills, salesBills, companyCurrency]);

    const getExportColumns = useMemo(() => {
        return [
            { key: 'itemName', label: 'Item Name' },
            { key: 'itemType', label: 'Type' },
            { key: 'unit', label: 'Unit' },
            { key: 'rate', label: `Rate (${getCurrencySymbol(companyCurrency)})` },
            { key: 'hsnCode', label: 'HSN Code' },
            { key: 'gstPercentage', label: 'GST %' },
            { key: 'stock', label: 'Stock' },
            { key: 'category', label: 'Category' },
            { key: 'barcode', label: 'Barcode' },
            { key: 'active', label: 'Status' }
        ];
    }, [companyCurrency]);

    const getReportDetails = useMemo(() => {
        return {
            'Total Items': sortedItems.length,
            'Active Items': sortedItems.filter(item => item.isActive !== false).length,
            'Items Type': filterType === 'All' ? 'All Types' : filterType,
            'GST Rate': filterGstRate === 'All' ? 'All Rates' : `${filterGstRate}%`,
            'Filter Active': filterActive === 'All' ? 'All Items' : filterActive
        };
    }, [sortedItems, filterType, filterGstRate, filterActive]);

    const activeItemCount = itemsList.filter((i) => i.isActive !== false).length;

    const getLiveStock = useCallback(
        (item) => {
            const opening = parseFloat(item.openingStock) || 0;
            const allPurchases = purchaseBills
                .flatMap((bill) => (bill.rows || []).filter((row) => row.item === item.id))
                .reduce((sum, row) => sum + (parseFloat(row.qty) || 0), 0);
            const allSales = salesBills
                .flatMap((bill) => (bill.rows || []).filter((row) => row.item === item.id))
                .reduce((sum, row) => sum + (parseFloat(row.qty) || 0), 0);
            return opening + allPurchases - allSales;
        },
        [purchaseBills, salesBills]
    );

    const toggleCard = (itemId) => {
        setExpandedCards((prev) => ({ ...prev, [itemId]: !prev[itemId] }));
    };

    const formProps = {
        editingItemId,
        companyCurrency,
        t,
        itemName,
        setItemName,
        quantityMeasurement,
        setQuantityMeasurement,
        unitSearch,
        setUnitSearch,
        filteredUnits,
        predefinedUnits,
        customUnit,
        setCustomUnit,
        defaultRate,
        setDefaultRate,
        itemType,
        setItemType,
        gstPercentage,
        setGstPercentage,
        hsnCode,
        setHsnCode,
        isActive,
        setIsActive,
        category,
        setCategory,
        customCategory,
        setCustomCategory,
        predefinedCategories,
        companyCategories,
        barcodeNumber,
        setBarcodeNumber,
        compositionGstRate,
        setCompositionGstRate,
        purchasePrice,
        setPurchasePrice,
        salePrice,
        setSalePrice,
        openingStock,
        setOpeningStock,
        description,
        setDescription,
        isRawMaterial,
        setIsRawMaterial,
        rawMaterialType,
        setRawMaterialType,
        defaultServiceTagsInput,
        setDefaultServiceTagsInput,
        onSave: handleSaveItem,
        onClear: clearItemForm,
    };

    const historyProps = {
        companyCurrency,
        companyDetails,
        getExportData,
        getExportColumns,
        getReportDetails,
        itemsList,
        filteredItems,
        pagination,
        sortConfig,
        handleSort,
        searchTerm,
        filterType,
        filterGstRate,
        filterActive,
        onSearchChange: setSearchTerm,
        onFilterTypeChange: setFilterType,
        onFilterGstRateChange: setFilterGstRate,
        onFilterActiveChange: setFilterActive,
        onClearFilters: clearFilters,
        getLiveStock,
        selectedForDeletion,
        onToggleSelect: handleDeleteSelection,
        onSelectAllPage: handleSelectAllForDeletion,
        onEdit: handleEditItem,
        onDelete: handleDeleteItem,
        onBulkDeleteClick: () => setShowBulkDeleteConfirm(true),
        isBulkDeleting,
        showMobileFilters,
        setShowMobileFilters,
        expandedCards,
        toggleCard,
        splitLayout: true,
    };

    const shouldShowLoading = isInitialLoading && db && userId && isAuthReady;
    
    if (shouldShowLoading) {
        return (
            <LoadingScreen 
                message="Loading Items..."
                subMessage="Fetching your product and service catalog"
                fullScreen={true}
            />
        );
    }

    const inactiveCount = Math.max(0, itemsList.length - activeItemCount);

    return (
        <div className={`flex flex-col w-full min-h-[calc(100dvh-4.75rem)] lg:h-[calc(100dvh-4.75rem)] overflow-visible lg:overflow-hidden ${ITEMS_THEME.pageBg} mobile-content`}>
            <div className="flex flex-col flex-1 min-h-0 w-full max-w-none overflow-visible lg:overflow-hidden px-2 sm:px-3 lg:px-4 2xl:px-5 pt-3 sm:pt-4 pb-2">
                <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-2 sm:gap-3 mb-2 sm:mb-3 shrink-0">
                    <div className="flex items-start gap-3 min-w-0">
                        <div
                            className={`w-11 h-11 rounded-xl flex items-center justify-center text-white flex-shrink-0 ${ITEMS_THEME.iconTile}`}
                        >
                            <Package className="w-5 h-5" aria-hidden />
                        </div>
                        <div className="min-w-0">
                            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">Items</h1>
                            <p className="text-sm text-slate-500 mt-0.5">{ITEMS_THEME.subtitle}</p>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 xl:flex-1 xl:max-w-none w-full">
                    <div className={ITEMS_THEME.statCardTotal}>
                        <div className="text-[11px] font-medium text-slate-500 uppercase tracking-wide">Total items</div>
                        <div className="text-xl font-bold text-amber-900 mt-1">{itemsList.length}</div>
                    </div>
                    <div className={ITEMS_THEME.statCardActive}>
                        <div className="text-[11px] font-medium text-emerald-700 uppercase tracking-wide">Active</div>
                        <div className="text-xl font-bold text-emerald-800 mt-1">{activeItemCount}</div>
                    </div>
                    <div className={ITEMS_THEME.statCardInactive}>
                        <div className="text-[11px] font-medium text-slate-500 uppercase tracking-wide">Inactive</div>
                        <div className="text-xl font-bold text-slate-700 mt-1">{inactiveCount}</div>
                    </div>
                    <div className={ITEMS_THEME.statCardFiltered}>
                        <div className="text-[11px] font-medium text-amber-800/90 uppercase tracking-wide">Showing</div>
                        <div className="text-xl font-bold text-amber-900 mt-1">{filteredItems.length}</div>
                    </div>
                    </div>
                </div>

                <div className="flex flex-col flex-1 min-h-0 w-full overflow-visible lg:overflow-hidden lg:min-h-[12rem]">
                <ItemsCatalogTab
                    formSectionRef={formSectionRef}
                    editingItemId={editingItemId}
                    filteredCount={filteredItems.length}
                    totalCount={itemsList.length}
                    activeCount={activeItemCount}
                    message={message}
                    formProps={formProps}
                    historyProps={historyProps}
                />
                </div>

                    {showBulkDeleteConfirm && (
                    <div
                        className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-0 sm:p-4"
                        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="bulk-delete-title"
                    >
                        <div className="bg-white rounded-t-2xl sm:rounded-2xl p-4 sm:p-6 max-w-md w-full shadow-xl border border-slate-200/80">
                            <h3 id="bulk-delete-title" className="text-lg font-semibold text-gray-900 mb-2">
                                Delete selected items?
                            </h3>
                            <p className="text-sm text-gray-600 mb-6">
                                This will permanently delete <strong>{selectedForDeletion.size}</strong> item(s). This
                                cannot be undone.
                            </p>
                            <div className="flex flex-col-reverse sm:flex-row gap-2">
                                <button
                                    type="button"
                                onClick={() => setShowBulkDeleteConfirm(false)}
                                    className="flex-1 min-h-[44px] px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
                                disabled={isBulkDeleting}
                            >
                                Cancel
                            </button>
                            <button
                                    type="button"
                                onClick={handleBulkDelete}
                                disabled={isBulkDeleting}
                                    className="flex-1 min-h-[44px] px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 font-medium"
                            >
                                    {isBulkDeleting ? 'Deleting...' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            </div>
        </div>
    );
};

export default withMPINProtection(Items, 'Items');
